import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Container,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'src/text';
import { fetchPublicLocation } from 'src/api/services/public-location-service';
import { BusinessLocation } from 'src/api/types/business-types';
import { styles } from './styles';
import { submitPublicCheckin } from 'src/api/services/public-checkin-service';

function getRedirectUrl(loc?: BusinessLocation): string | undefined {
  if (!loc) return undefined;
  const meta = loc.metadata;
  return (
    meta?.newReviewUri ||
    meta?.mapsUri ||
    (meta?.placeId
      ? `https://search.google.com/local/writereview?placeid=${meta.placeId}`
      : undefined)
  );
}

function toHostname(url?: string): string | undefined {
  try {
    if (!url) return undefined;
    const u = new URL(url);
    return u.hostname;
  } catch {
    return undefined;
  }
}

const ReviewLandingScreen: React.FC = () => {
  const { locationId } = useParams();
  const { reviewLandingLabels, genericLabels } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [location, setLocation] = useState<BusinessLocation | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  // Fetch location using only the locationId from route (public read)
  useEffect(() => {
    const run = async () => {
      if (!locationId) return;
      setLoading(true);
      setLoadError(undefined);
      try {
        const data = await fetchPublicLocation(locationId);
        if (data) {
          setLocation(data as BusinessLocation);
        } else {
          setLoadError('Location not found');
        }
      } catch (e) {
        setLoadError('Failed to load location');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [locationId]);

  const host = useMemo(
    () => toHostname(location?.websiteUri),
    [location?.websiteUri],
  );
  type WithSettings = BusinessLocation & {
    settings?: { reviewLandingMessage?: string };
  };
  const photoUrl = location?.metadata?.photoUrl;
  const displayedTitle = useMemo(
    () => location?.title || location?.name || host || 'Business',
    [location?.title, location?.name, host],
  );
  const redirectUrl = useMemo(() => getRedirectUrl(location), [location]);
  const displayMessage = useMemo(() => {
    const text = (
      (location as WithSettings | undefined)?.settings?.reviewLandingMessage ||
      ''
    ).trim();
    return text.length
      ? text
      : reviewLandingLabels.fallbackMessage || 'Review and get 20% discount';
  }, [location, reviewLandingLabels.fallbackMessage]);

  const disabled = useMemo(
    () => !redirectUrl || submitting,
    [redirectUrl, submitting],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    if (!redirectUrl) {
      setError(reviewLandingLabels.missingUrl || 'Missing review URL.');
      return;
    }
    try {
      setSubmitting(true);
      // Persist check-in via public API (truly non-blocking using sendBeacon/keepalive)
      if (locationId) {
        submitPublicCheckin({ locationId, name, email, optIn });
      }
      window.location.href = redirectUrl;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Container maxWidth="sm">
        <Paper elevation={2} sx={styles.card}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
              }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {photoUrl ? (
                <Box
                  component="img"
                  src={photoUrl}
                  alt={location?.title || 'Photo'}
                  sx={styles.hero}
                />
              ) : null}
              <Stack spacing={3} alignItems="center">
                {loadError ? (
                  <Typography variant="body2" color="error" role="alert">
                    {loadError}
                  </Typography>
                ) : null}
                <Box sx={styles.centerText}>
                  <Typography variant="h5" color="text.primary" gutterBottom>
                    {displayedTitle}
                  </Typography>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={styles.headingTitle}>
                    {displayMessage}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reviewLandingLabels.headingInfo}
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
                  <Stack spacing={2}>
                    <TextField
                      label={reviewLandingLabels.fieldName}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      fullWidth
                      inputProps={{ autoComplete: 'name' }}
                      InputLabelProps={{ sx: styles.inputLabel }}
                      sx={styles.textField}
                      required
                    />
                    <TextField
                      label={reviewLandingLabels.fieldEmail}
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      fullWidth
                      inputProps={{ autoComplete: 'email' }}
                      InputLabelProps={{ sx: styles.inputLabel }}
                      sx={styles.textField}
                      required
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={optIn}
                          onChange={(_, v) => setOptIn(v)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          {reviewLandingLabels.optIn}
                        </Typography>
                      }
                    />
                    {error ? (
                      <Typography variant="body2" color="error" role="alert">
                        {error}
                      </Typography>
                    ) : null}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={disabled}
                      size="large"
                      fullWidth
                      sx={styles.submitButton}>
                      {reviewLandingLabels.continue}
                    </Button>
                  </Stack>
                </Box>

                <Divider flexItem />
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {reviewLandingLabels.termsPrefix}{' '}
                    <RouterLink to="/terms-of-use">
                      {genericLabels.terms}
                    </RouterLink>{' '}
                    {genericLabels.and}{' '}
                    <RouterLink to="/privacy-policy">
                      {genericLabels.privacy}
                    </RouterLink>
                    .
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {reviewLandingLabels.poweredBy}{' '}
                    <a
                      href="https://neberox.com"
                      target="_blank"
                      rel="noreferrer">
                      Neberox
                    </a>
                  </Typography>
                </Stack>
              </Stack>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export { ReviewLandingScreen };
